/**
 * Turns a drop or a file-input change into a flat list of files that still know
 * where they came from.
 *
 * The point is to preserve the folder structure the user already built on their
 * own machine, instead of flattening it and re-deriving categories.
 */

export type CollectedFile = {
  file: File;
  /** Path relative to the drop root, e.g. `"Awards/2024/prize.pdf"`. */
  relativePath: string;
};

type FileSystemEntryLike = {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  file?: (
    onSuccess: (file: File) => void,
    onError: (error: Error) => void
  ) => void;
  createReader?: () => {
    readEntries: (
      onSuccess: (entries: FileSystemEntryLike[]) => void,
      onError: (error: Error) => void
    ) => void;
  };
};

const joinPath = (prefix: string, name: string) =>
  prefix ? `${prefix}/${name}` : name;

const readFile = (entry: FileSystemEntryLike): Promise<File | null> =>
  new Promise((resolve) => {
    if (!entry.file) {
      resolve(null);
      return;
    }
    entry.file(
      (file) => resolve(file),
      () => resolve(null)
    );
  });

/**
 * `readEntries` returns at most 100 entries per call and signals the end of the
 * directory with an empty batch, so a single call silently truncates large
 * folders.
 */
const readAllEntries = (
  entry: FileSystemEntryLike
): Promise<FileSystemEntryLike[]> =>
  new Promise((resolve) => {
    const reader = entry.createReader?.();
    if (!reader) {
      resolve([]);
      return;
    }
    const collected: FileSystemEntryLike[] = [];
    const readBatch = () => {
      reader.readEntries(
        (batch) => {
          if (batch.length === 0) {
            resolve(collected);
            return;
          }
          collected.push(...batch);
          readBatch();
        },
        () => resolve(collected)
      );
    };
    readBatch();
  });

const walkEntry = async (
  entry: FileSystemEntryLike,
  prefix: string
): Promise<CollectedFile[]> => {
  if (entry.isFile) {
    const file = await readFile(entry);
    return file ? [{ file, relativePath: joinPath(prefix, entry.name) }] : [];
  }
  if (!entry.isDirectory) return [];

  const children = await readAllEntries(entry);
  const nested = await Promise.all(
    children.map((child) => walkEntry(child, joinPath(prefix, entry.name)))
  );
  return nested.flat();
};

/**
 * Collects files from an `<input type="file">` change. A `webkitdirectory`
 * input populates `webkitRelativePath`, which already carries the folder path.
 */
export const collectFileList = (
  files: FileList | File[] | null | undefined
): CollectedFile[] => {
  if (!files) return [];
  return Array.from(files).map((file) => ({
    file,
    relativePath: file.webkitRelativePath || file.name
  }));
};

/**
 * Collects files from a drop, descending into any dropped folders.
 *
 * The `webkitGetAsEntry()` calls have to happen before the first `await`: a
 * `DataTransfer` is only valid during the synchronous part of the drop handler,
 * and the browser empties `items` afterwards. Once we hold the entry objects
 * the traversal can be async.
 */
export const collectDroppedFiles = async (
  dataTransfer: DataTransfer
): Promise<CollectedFile[]> => {
  const items = dataTransfer.items ? Array.from(dataTransfer.items) : [];
  const entries = items
    .map((item) =>
      typeof item.webkitGetAsEntry === "function"
        ? (item.webkitGetAsEntry() as FileSystemEntryLike | null)
        : null
    )
    .filter((entry): entry is FileSystemEntryLike => Boolean(entry));

  // Safari and older browsers hand us files but no entries; fall back to the
  // flat list rather than dropping the upload.
  if (entries.length === 0) {
    return collectFileList(dataTransfer.files);
  }

  const collected = await Promise.all(
    entries.map((entry) => walkEntry(entry, ""))
  );
  return collected.flat();
};

/** Drops macOS/Windows folder metadata that no one wants as an exhibit. */
const IGNORED_NAMES = new Set([
  ".DS_Store",
  "Thumbs.db",
  "desktop.ini",
  ".localized"
]);

export const withoutSystemFiles = (files: CollectedFile[]): CollectedFile[] =>
  files.filter((entry) => {
    const segments = entry.relativePath.split("/");
    const name = segments[segments.length - 1];
    if (IGNORED_NAMES.has(name)) return false;
    // A resource fork or a hidden dotfile inside a dropped folder.
    return !name.startsWith("._") && !segments.some((s) => s === "__MACOSX");
  });
